import React, { useState } from 'react';
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Button,
  Form,
  FormFeedback,
  Alert,
  Spinner,
} from 'reactstrap';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import ParticlesAuth from '../AuthenticationInner/ParticlesAuth';
import { authApi } from '../../services/api';

const ForgotPassword = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: '',
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email('Please enter a valid email address')
        .required('Please enter your email'),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setError(null);

        await authApi.forgotPassword(values.email);
        setSuccess(true);
      } catch (err: any) {
        setError(err.message || 'Network error. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    },
  });

  document.title = 'Forgot Password | Coderom';

  return (
    <React.Fragment>
      <ParticlesAuth>
        <div className="auth-page-content mt-lg-5">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center mt-sm-5 mb-4 text-white-50">
                  <div>
                    <Link to="/" className="d-inline-flex align-items-center gap-2 text-decoration-none">
                      <img src="/logo-full-white.png" alt="Coderom" style={{ height: '50px', width: 'auto' }} />
                    </Link>
                  </div>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={8} lg={6} xl={5}>
                <Card className="mt-4 card-bg-fill">
                  <CardBody className="p-4">
                    <div className="text-center mt-2">
                      <h5 className="text-primary">Forgot Password?</h5>
                      <p className="text-muted">
                        Enter your email and we'll send you a link to reset your password
                      </p>

                      <div className="mt-4 pt-2">
                        <div className="avatar-lg mx-auto">
                          <div className="avatar-title bg-light text-primary display-5 rounded-circle">
                            <i className="ri-mail-lock-line"></i>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Success Message */}
                    {success ? (
                      <Alert color="success" className="mt-4 text-center">
                        <i className="ri-mail-check-line fs-24 d-block mb-2"></i>
                        <h5 className="alert-heading">Check your email!</h5>
                        <p className="mb-0">
                          If an account with that email exists, we've sent a password reset link.
                          Please check your inbox and spam folder.
                        </p>
                        <hr />
                        <Link to="/login" className="btn btn-sm btn-success mt-2">
                          <i className="ri-arrow-left-line me-1"></i>
                          Back to Login
                        </Link>
                      </Alert>
                    ) : (
                      <>
                        {/* Error Alert */}
                        {error && (
                          <Alert color="danger" className="mt-3">
                            <i className="ri-error-warning-line me-2"></i>
                            {error}
                          </Alert>
                        )}

                        <div className="p-2 mt-4">
                          <Form
                            onSubmit={(e) => {
                              e.preventDefault();
                              validation.handleSubmit();
                              return false;
                            }}
                          >
                            {/* Email */}
                            <div className="mb-4">
                              <Label htmlFor="email" className="form-label">
                                Email Address
                              </Label>
                              <Input
                                name="email"
                                className="form-control"
                                placeholder="Enter your email address"
                                type="email"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.email || ''}
                                invalid={
                                  validation.touched.email && validation.errors.email
                                    ? true
                                    : false
                                }
                              />
                              {validation.touched.email && validation.errors.email ? (
                                <FormFeedback type="invalid">
                                  {validation.errors.email}
                                </FormFeedback>
                              ) : null}
                            </div>

                            {/* Submit Button */}
                            <div className="mt-4">
                              <Button
                                color="primary"
                                disabled={loading}
                                className="btn btn-primary w-100"
                                type="submit"
                              >
                                {loading ? (
                                  <>
                                    <Spinner size="sm" className="me-2" />
                                    Sending...
                                  </>
                                ) : (
                                  <>
                                    <i className="ri-mail-send-line me-1"></i>
                                    Send Reset Link
                                  </>
                                )}
                              </Button>
                            </div>
                          </Form>
                        </div>
                      </>
                    )}
                  </CardBody>
                </Card>

                {/* Back to Login */}
                <div className="mt-4 text-center">
                  <p className="mb-0">
                    Remember your password?{' '}
                    <Link
                      to="/login"
                      className="fw-semibold text-primary text-decoration-underline"
                    >
                      Sign In
                    </Link>
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default ForgotPassword;
